// src/components/UsuariosTemporales/UsuariosTemporales.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CrearUsuarioTemporal from "./CrearUsuarioTemporal";
import AdministrarUsuariosTemporales from "./AdministrarUsuariosTemporales";
import { usePermisos } from "../../context/PermisosContext";

/* ===== helpers permisos (lee bitset guardado) ===== */


// Requisitos por pestaña
const TAB_REQUIRE = {
  crear: ["USUARIOS_TEMP_TAB_CREAR_VIEW", "USUARIOS_TEMP_TAB_CREAR_EDIT"],
  administrar: ["USUARIOS_TEMP_TAB_EDITAR_VIEW"],
};

const ALL_TABS = [
  { id: "crear", label: "Crear Usuario" },
  { id: "administrar", label: "Administrar Usuarios" },
];

const UsuariosTemporales = ({ initialSub, onSubChange }) => {
  const {can:hasBit}=usePermisos();
  const [searchParams, setSearchParams] = useSearchParams();

  // Gate global de la ventana (la ruta ya lo protege; esto es por robustez)
  const canViewWindow = hasBit("USUARIOS_TEMP_VIEW") ?? true;

  // Tabs visibles según permisos
  const visibleTabs = useMemo(() => {
    const allowIfUndefined = (keys) =>
      (keys || []).every((k) => {
        const v = hasBit(k);
        return v === true;
      });

    return ALL_TABS.filter((t) => allowIfUndefined(TAB_REQUIRE[t.id]));
  }, [hasBit]);

  // --- Fuente de verdad: URL ---
  const subFromUrl = searchParams.get("sub") || "";

  const computeValidTab = (candidate) => {
    if (!candidate) return visibleTabs[0]?.id || null;
    return visibleTabs.some((t) => t.id === candidate)
      ? candidate
      : visibleTabs[0]?.id || null;
  };

  // Decide tab inicial: URL > prop inicial > fallback
  const initialTab = computeValidTab(subFromUrl || initialSub || "crear");

  const [tab, setTab] = useState(initialTab);

  // Si cambian permisos/URL/prop, revalidar
  useEffect(() => {
    const next = computeValidTab(subFromUrl || initialSub || tab);
    if (next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, subFromUrl, initialSub]);

  // Espejo estado -> URL y notificar al padre (sin loops)
  useEffect(() => {
    if (!tab) return;
    const next = new URLSearchParams(searchParams);
    if (next.get("sub") !== tab) {
      next.set("sub", tab);
      setSearchParams(next, { replace: true });
    }
    onSubChange?.(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  if (!canViewWindow) {
    return (
      <div className="bg-neutral-900 text-white p-4 sm:p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
          GESTIÓN DE USUARIOS TEMPORALES
        </h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">No tienes permiso para ver esta sección.</p>
        </div>
      </div>
    );
  }

  if (visibleTabs.length === 0 || !tab) {
    return (
      <div className="bg-neutral-900 text-white p-4 sm:p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
        <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
          GESTIÓN DE USUARIOS TEMPORALES
        </h1>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4 text-center">
          <p className="text-sm text-neutral-300">
            No hay pestañas disponibles con tus permisos actuales.
          </p>
        </div>
      </div>
    );
  }

  const renderContenido = () => {
    switch (tab) {
      case "crear":
        return <CrearUsuarioTemporal />;
      case "administrar":
        return <AdministrarUsuariosTemporales />;
      default:
        return null;
    }
  };

  return (
    <div className="bg-neutral-900 text-white p-4 sm:p-6 rounded-lg w-full min-h-[50dvh] md:min-h-screen">
      <h1 className="text-center text-2xl sm:text-3xl font-bold text-green-500 mb-4 sm:mb-6">
        GESTIÓN DE USUARIOS TEMPORALES
      </h1>

      {/* Tabs (solo visibles) */}
      <div className="mb-4 sm:mb-6 flex justify-center">
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`w-full sm:w-auto px-3 sm:px-4 py-2 rounded text-sm sm:text-base transition-all duration-200 ${
                tab === t.id
                  ? "bg-green-500 text-black"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 sm:mt-6">{renderContenido()}</div>
    </div>
  );
};

export default UsuariosTemporales;
